// Quotes: every quote, with each version kept, so the customer can see what
// changed between them (v1 → v2) and download each PDF, and buy the latest
// with the demo card on file. A quote without a version (an older record)
// shows as version 1. /quotes/:quoteId opens with that quote highlighted.
import { useEffect, useState } from 'react';
import { CreditCard, Download, ShieldCheck } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, documentUrl, ok } from '@cover/core/api/client.ts';
import type { Quote } from '@cover/core/api/types.ts';
import { useBrand, whatsappLink } from '@cover/core/brand.tsx';
import { cn } from '@cover/core/cn.ts';
import { date, money } from '@cover/core/format.ts';
import { isFresh } from '@cover/core/live.ts';
import { Badge, type Tone } from '@cover/core/ui/Badge.tsx';
import { Card, CardBody, CardHeader, Row } from '@cover/core/ui/Card.tsx';
import { Button } from '@cover/core/ui/Button.tsx';
import { Dialog } from '@cover/core/ui/Dialog.tsx';
import { Field, Input } from '@cover/core/ui/Form.tsx';
import { useToast } from '@cover/core/ui/Toast.tsx';
import { Empty, Load } from '@cover/core/ui/States.tsx';
import { api, keys, usePaymentMethod, useQuotes } from '../api.ts';
import { useBusinessId } from '../session.tsx';
import { cardLabel } from './Payment.tsx';
import { PageTitle } from '../Layout.tsx';

const STATUS: Record<Quote['status'], { label: string; tone: Tone }> = {
  quoted: { label: 'Quoted', tone: 'info' },
  purchased: { label: 'Bought', tone: 'ok' },
  referred: { label: 'With our team', tone: 'neutral' },
  declined: { label: 'Declined', tone: 'bad' },
  expired: { label: 'Expired', tone: 'neutral' },
};

type Group = { reference: string; versions: Quote[] };

function group(quotes: Quote[]): Group[] {
  const byRef = new Map<string, Quote[]>();
  for (const q of quotes) {
    const ref = q.reference ?? q.quoteId;
    byRef.set(ref, [...(byRef.get(ref) ?? []), q]);
  }
  return [...byRef.entries()]
    .map(([reference, versions]) => ({ reference, versions: versions.sort((a, b) => (b.version ?? 1) - (a.version ?? 1)) }))
    .sort((a, b) => b.versions[0]!.createdAt.localeCompare(a.versions[0]!.createdAt));
}

export function QuotesPage() {
  const { quoteId } = useParams();
  const quotes = useQuotes();
  const brand = useBrand();
  const wa = whatsappLink(brand, 'Can I get a quote?');
  return (
    <>
      <PageTitle lead="Every quote we’ve given you. When you change something, we keep each version so you can compare.">Quotes</PageTitle>
      <Load q={quotes}>
        {(list) =>
          list.length ? (
            <div className="space-y-5">
              {group(list).map((g) => (
                <QuoteGroup key={g.reference} g={g} focus={!!quoteId && g.versions.some((v) => v.quoteId === quoteId)} />
              ))}
            </div>
          ) : (
            <Card>
              <Empty title="No quotes yet">{wa ? <a href={wa}>Ask for one in WhatsApp</a> : 'Ask for one in WhatsApp.'}</Empty>
            </Card>
          )
        }
      </Load>
    </>
  );
}

function QuoteGroup({ g, focus }: { g: Group; focus: boolean }) {
  const [latest, ...earlier] = g.versions as [Quote, ...Quote[]];
  const status = STATUS[latest.status];
  const buyable = latest.status === 'quoted' && (!latest.validUntil || latest.validUntil >= new Date().toISOString().slice(0, 10));
  useEffect(() => {
    if (focus) document.getElementById(`quote-${g.reference}`)?.scrollIntoView({ block: 'start' });
  }, [focus, g.reference]);
  return (
    <Card id={`quote-${g.reference}`} className={cn('scroll-mt-24', (focus || isFresh(latest.createdAt)) && 'ring-2 ring-brand')}>
      <CardHeader
        title={`Quote ${g.reference}`}
        description={`Version ${latest.version ?? 1} · ${date(latest.createdAt)}`}
        action={
          <div className="flex flex-wrap justify-end gap-1.5">
            {isFresh(latest.createdAt) && <Badge tone="brand">New</Badge>}
            <Badge tone={status.tone}>{status.label}</Badge>
          </div>
        }
      />
      <CardBody className="space-y-4">
        {latest.premium && (
          <p>
            <span className="text-3xl font-bold">{money(latest.premium.annualPence)}</span> <span className="text-mid">a year</span>
            {latest.premium.monthlyPence ? <span className="block text-sm text-mid">or {money(latest.premium.monthlyPence)} a month</span> : null}
          </p>
        )}
        {!!latest.changes?.length && <Changes q={latest} />}
        <dl className="divide-y divide-line">
          {latest.covers?.map((c) => (
            <Row key={c.name} label={c.name}>
              {c.limit}
            </Row>
          ))}
          {latest.excessPence != null && <Row label="Excess">{money(latest.excessPence)}</Row>}
          {latest.validUntil && latest.status === 'quoted' && <Row label="Price valid until">{date(latest.validUntil)}</Row>}
        </dl>
        {latest.decline?.reason && <p className="rounded-md bg-bad-bg p-3 text-sm text-bad-fg">{latest.decline.reason}</p>}
        {buyable && <Buy q={latest} />}
        <div className="flex flex-wrap items-center gap-3">
          {latest.documentId && <PdfLink id={latest.documentId} label={`Quote PDF (version ${latest.version ?? 1})`} />}
          {latest.status === 'purchased' && <Link to="/policies">See the policy</Link>}
          <span className="text-xs text-muted">Demo, not a real quote.</span>
        </div>

        {earlier.length > 0 && (
          <details className="rounded-md border border-line px-3 py-2">
            <summary className="cursor-pointer py-1 font-semibold">Earlier versions ({earlier.length})</summary>
            <ul className="divide-y divide-line">
              {earlier.map((v) => (
                <li key={v.quoteId} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
                  <span>
                    <strong>Version {v.version ?? 1}</strong> · {date(v.createdAt)}
                    {v.premium && <> · {money(v.premium.annualPence)} a year</>}
                    <span className="block text-mid">{v.covers?.map((c) => `${c.name} ${c.limit}`).join(' · ')}</span>
                  </span>
                  {v.documentId && <PdfLink id={v.documentId} label="PDF" />}
                </li>
              ))}
            </ul>
          </details>
        )}
      </CardBody>
    </Card>
  );
}

/** What changed from the previous version, old value struck through, new one highlighted. */
function Changes({ q }: { q: Quote }) {
  return (
    <section aria-label={`What changed from version ${(q.version ?? 2) - 1}`} className="rounded-lg bg-warn-bg/60 p-3">
      <h3 className="mb-2 text-sm font-semibold">What changed from version {(q.version ?? 2) - 1}</h3>
      <ul className="space-y-1.5 text-sm">
        {q.changes!.map((c) => (
          <li key={c.item} className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-medium">{c.item}:</span>
            {c.from === null ? (
              <span>added</span>
            ) : (
              <del className="text-mid">{c.from}</del>
            )}
            <span aria-hidden>→</span>
            {c.to === null ? <span>removed</span> : <ins className="rounded bg-white px-1.5 font-bold no-underline">{c.to}</ins>}
          </li>
        ))}
      </ul>
    </section>
  );
}

function PdfLink({ id, label }: { id: string; label: string }) {
  return (
    <a href={documentUrl(id)} target="_blank" rel="noopener" className="inline-flex min-h-11 items-center gap-2 font-semibold">
      <Download className="h-4 w-4" aria-hidden /> {label}
    </a>
  );
}

/** Buy with the demo card on file, or add one first. */
function Buy({ q }: { q: Quote }) {
  const card = usePaymentMethod();
  const [open, setOpen] = useState(false);
  if (card.isLoading) return null;
  const c = card.data;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-card bg-surface p-4">
      <div className="min-w-0 flex-1 basis-56 text-sm">
        {c ? (
          <p className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-mid" aria-hidden /> Pay with {cardLabel(c)} <Badge tone="info">Test card</Badge>
          </p>
        ) : (
          <p className="text-mid">Add a test card to buy this cover. It’s a demo: no money moves.</p>
        )}
      </div>
      {c ? (
        <Button onClick={() => setOpen(true)}>Buy this cover</Button>
      ) : (
        <Button asChild>
          <Link to={`/account/payment?next=${encodeURIComponent(`/quotes/${q.quoteId}`)}`} className="text-on-brand no-underline hover:no-underline">
            Add a card
          </Link>
        </Button>
      )}
      {open && c && <BuyDialog q={q} cardText={cardLabel(c)} onClose={() => setOpen(false)} />}
    </div>
  );
}

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n: number) => new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

const PURCHASE_ERRORS: Record<string, string> = {
  already_purchased: 'You’ve already bought this quote.',
  not_buyable: 'This quote can’t be bought online. Message us and we’ll help.',
  superseded: 'There’s a newer version of this quote. Buy that one instead.',
  quote_expired: 'This price has expired. Ask us for a fresh quote.',
  invalid_start_date: 'Pick a start date between today and 30 days from now.',
};

function BuyDialog({ q, cardText, onClose }: { q: Quote; cardText: string; onClose: () => void }) {
  const b = useBusinessId();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(today());
  const [agree, setAgree] = useState(false);
  const buy = useMutation({
    mutationFn: () => ok(api.POST('/quotes/{quoteId}/purchase', { params: { path: { quoteId: q.quoteId } }, body: { acceptTerms: true, startDate } })),
    onSuccess: async (r) => {
      await Promise.all([keys.quotes(b), keys.policies(b), keys.profile(b), keys.documents(b), keys.next].map((queryKey) => qc.invalidateQueries({ queryKey })));
      toast.ok(`You’re covered from ${date(r.startDate)}. Policy ${r.policyNumber}${r.emailed ? '; we’ve emailed your documents' : ''}.`);
      onClose();
      navigate('/policies');
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 402) {
        onClose();
        navigate(`/account/payment?next=${encodeURIComponent(`/quotes/${q.quoteId}`)}`);
      } else toast.fail(e instanceof ApiError && PURCHASE_ERRORS[e.code] ? new Error(PURCHASE_ERRORS[e.code]) : e);
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Buy this cover" description="Demo: nothing is charged and the policy isn’t real.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (agree) buy.mutate();
        }}
      >
        <dl className="divide-y divide-line rounded-card px-4 ring-1 ring-inset ring-line">
          <Row label="Quote">{q.reference ?? q.quoteId}</Row>
          {q.premium && <Row label="Price">{money(q.premium.annualPence)} a year</Row>}
          <Row label="Paying with">{cardText}</Row>
        </dl>
        <Field label="Start date" hint="Today, or up to 30 days from now">
          <Input type="date" min={today()} max={plusDays(30)} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <label className="flex min-h-11 cursor-pointer items-start gap-3 text-sm">
          <input type="checkbox" className="mt-0.5 h-5 w-5 accent-[rgb(var(--brand))]" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          <span>I’ve checked the details on this quote are right, and I’ve read the quote PDF.</span>
        </label>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" busy={buy.isPending} disabled={!agree}>
            <ShieldCheck className="h-4 w-4" aria-hidden /> Buy for {q.premium ? money(q.premium.annualPence) : 'this price'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Not now
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
